import { defineComponent as _defineComponent } from 'vue'
import {
    ref,
    reactive,
    computed,
    onMounted,
    onUnmounted,
    onBeforeMount,
  } from "vue";
  import store from "@/store";
  import router from "@/router";
  import HeaderCustomer from "@/components/HeaderCustomer.vue";
  import EOIInfoMessage from "@/components/eoi/EOIInfoMessage.vue";
  import EOIDontHaveSolicitor from "@/components/eoi/EOIDontHaveSolicitor.vue";
  import LotCard from "@/components/eoi/LotCard.vue";
  import Cleave from "vue-cleave-component";
  import useVuelidate from "@vuelidate/core";
  import {
    required,
    email,
    requiredIf,
    numeric,
    minLength,
    minValue,
  } from "@vuelidate/validators";
  import { helpers } from "@vuelidate/validators";
  import { validPhoneNumber } from "@/helpers/validation";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import DropDownFile from "@/components/ui/dropdown/DropDownFile.vue";
  import CheckboxInput from "@/components/ui/input/CheckboxInput.vue";
  import { allowedDocumentMimeTypes, MaxFileSize } from "@/constants";
  import ConfirmationModal from "@/components/ui/modals/RemoveDocumentModal.vue";
  import { EoiStatus } from "@/enums/EoiStatus";
  import { scrollTo } from "@/helpers/smoothScrollToElement";
  import { phoneNumberOption } from "@/helpers/cleavePlugin";
  import { LotInterface } from "@/types/LotInterface";
  import {
    ContractConditions,
    Conveyancer,
    ResponseEoiDetails,
  } from "@/types/CustomerLotDetails";
  import {
    changePrimaryColor,
    resetPrimaryColor,
  } from "@/helpers/customer/changePrimaryColor";
  import { LotStatus } from "@/enums/LotStatus";
  import { POSITION, useToast } from "vue-toastification";
  import InputFile from "@/components/ui/input/InputFile.vue";
  import { validDocumentsTypes } from "@/helpers/fileUpload";
  import { isLogged } from "@/features/useUser";
  import LocalStorageService from "@/services/localStorage.service";
  import { AxiosError, AxiosResponse } from "axios";
  import { EoiHttpMessageErrors } from "@/constants/eoiHttpMessageErrors";
  import { formatPrice } from "@/helpers/transformation/price";
  import { EoiTemplateGroups } from "@/enums/EoiTemplateGroups";
  import { EoiTemplateFields } from "@/enums/EoiTemplateFields";
  import { StatesEnum } from "@/enums/StatesEnum";
  import InfoMessage from "@/components/ui/InfoMessage.vue";
  import getUserRole from "@/helpers/getUserRole";
  import {
    getEmptyConveyancerDetails,
    getEoiRequestForm,
    getZipConveyancerDetails,
  } from "@/helpers/eoi";
  import { capitalizeString } from "@/helpers/transformation/string";
  import { changePrimaryColorEoiViews } from "@/helpers/eoi/changePrimaryColorEoiViews";
  interface Documents {
    name: string;
    link: string;
    documentType: string;
    modifiedDate: string;
  }
  interface EoiUserProfile {
    firstName?: string;
    middleName?: string;
    lastName?: string;
    phone?: string;
    detailsId?: {
      _id?: string;
      address?: string;
      postcode?: string;
      state?: string;
    };
  }
  interface Props {
    estateId: string;
    stageId: string;
    lotId: string;
    eoiId: string;
    isPreview: boolean;
    eoiTemplate: Record<string, unknown>;
    businessId: string;
  }
  const fileTypeError = "Only png, jpg, jpeg, heic, pdf files allowed";
  const documentPrivacyTip =
    "For privacy, sensitive data is automatically removed after 30 days.";
  
export default /*@__PURE__*/_defineComponent({
  __name: 'EOIRequestForm',
  props: {
    estateId: {},
    stageId: {},
    lotId: {},
    eoiId: {},
    isPreview: { type: Boolean },
    eoiTemplate: {},
    businessId: {}
  },
  emits: ["update:isCreateEoiError"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

  const props = __props;

  const emits = __emit;

  const { isCustomer } = getUserRole();
  const toast = useToast();
  const isCreateEioError = ref(false);
  const isSecondBuyer = ref(false);
  const subjectToAny = ref(false);
  const customerId = ref(
    isLogged()
      ? store.state.user?.data?._id
      : store.state.eoi?.unregisteredCustomerId
  );
  const eoiId = ref(props.eoiId);
  const estate = ref({});
  const lot = ref<LotInterface | null>(null);
  const acceptedTypes = validDocumentsTypes.join(", ");
  const showConfirmationModal = ref(false);
  const loading = ref(false);
  const isEmailBelongsToCustomer = ref(true);
  const hasInitialHoldEmailError = ref(false);
  const userProfile = ref<EoiUserProfile>({});
  const buyerPassportList = ref([]);
  const buyerDriverList = ref([]);
  const documentList = reactive({
    firstBuyer: [],
    secondBuyer: [],
    bank: [],
    all: [],
  });

  const hasFirstBuyerMiddleName = ref(true);
  const hasSecondBuyerMiddleName = ref(true);

  const fileSizeError = `File size should not exceed ${Math.floor(
    MaxFileSize / 1048576
  )} MB`;
  const imageUploadTips = `Only PNG, JPG, JPEG, HEIC and PDF files with max size of ${Math.floor(
    MaxFileSize / 1048576
  )} MB`;
  const transitionName = ref("fade");

  const fileInfo = reactive({
    firstBuyer: {
      isError: false,
      errorMessage: "",
    },
    secondBuyer: {
      isError: false,
      errorMessage: "",
    },
    bank: {
      isError: false,
      errorMessage: "",
    },
  });

  const getEoiTemplateGroups = computed(() => {
    return eoiTemplate?.value.groups;
  });

  const getTemplateField = (groupName: string, fieldName: string) => {
    return getEoiTemplateGroups?.value
      ?.filter((group: any) => group.groupName === groupName)[0]
      .fields.filter((field: any) => field.fieldName === fieldName)[0]
      ?.isActive;
  };

  const eoiTemplate = ref({});
  const requestAmlInformation = computed(
    () =>
      (
        eoiTemplate.value as {
          requestAmlInformation?: boolean;
        }
      ).requestAmlInformation !== false
  );
  const formLoading = ref(false);
  const updatedEoiFormObject = ref({});
  const isZeroDeposit = ref(false);
  const isDontHaveSolicitorOpen = ref(false);
  const haveOwnSolicitor = ref(true);
  const isTermsAccepted = ref(false);
  const purchaserTypeError = ref(false);

  const fileInfoValid = computed(() => {
    if (isSecondBuyer.value) {
      return (
        !fileInfo.firstBuyer.isError &&
        !fileInfo.secondBuyer.isError &&
        !fileInfo.bank.isError
      );
    } else {
      return !fileInfo.firstBuyer.isError && !fileInfo.bank.isError;
    }
  });

  const isLotStatusAvailable = computed(() => {
    return (
      lot.value?.status === LotStatus.AVAILABLE ||
      lot.value?.status === LotStatus.HOLD
    );
  });

  let requestForm = reactive(getEoiRequestForm());

  const isPurchasingByCompany = computed(
    () =>
      requestAmlInformation.value &&
      ["Company", "Trust / SMSF", "Partnership"].includes(
        requestForm.purchaserType
      )
  );

  const purchaserTypes = [
    "Individual",
    "Sole Trader",
    "Company",
    "Trust / SMSF",
    "Partnership",
  ];

  const sourceOfDepositFundsOptions = [
    "Personal Savings",
    "Inheritance",
    "Sale of Other Property",
    "Gift from Family",
    "Bank Loan/Mortgage",
    "Overseas Transfer",
    "Business Proceeds",
    "Other (Please Specify)",
  ];

  const countryOptions = (() => {
    const regionCodes =
      "AF AL DZ AS AD AO AI AQ AG AR AM AW AU AT AZ BS BH BD BB BY BE BZ BJ BM BT BO BQ BA BW BV BR IO BN BG BF BI CV KH CM CA KY CF TD CL CN CX CC CO KM CG CD CK CR CI HR CU CW CY CZ DK DJ DM DO EC EG SV GQ ER EE SZ ET FK FO FJ FI FR GF PF TF GA GM GE DE GH GI GR GL GD GP GU GT GG GN GW GY HT HM VA HN HK HU IS IN ID IR IQ IE IM IL IT JM JP JE JO KZ KE KI KP KR KW KG LA LV LB LS LR LY LI LT LU MO MG MW MY MV ML MT MH MQ MR MU YT MX FM MD MC MN ME MS MA MZ MM NA NR NP NL NC NZ NI NE NG NU NF MK MP NO OM PK PW PS PA PG PY PE PH PN PL PT PR QA RE RO RU RW BL SH KN LC MF PM VC WS SM ST SA SN RS SC SL SG SX SK SI SB SO ZA GS SS ES LK SD SR SJ SE CH SY TW TJ TZ TH TL TG TK TO TT TN TR TM TC TV UG UA AE GB US UM UY UZ VU VE VN VG VI WF EH YE ZM ZW".split(
        " "
      );
    const displayNames = new Intl.DisplayNames(["en"], { type: "region" });

    return regionCodes.map((code) => displayNames.of(code)).sort();
  })();

  const isEntityPurchaser = computed(() =>
    ["Company", "Trust / SMSF", "Partnership"].includes(
      requestForm.purchaserType
    )
  );

  const amlBuyers = computed(() => {
    if (!["Individual", "Sole Trader"].includes(requestForm.purchaserType)) {
      return [];
    }

    const buyers = [
      {
        key: "firstBuyer",
        label: "Buyer 1",
        data: requestForm.amlDetails.firstBuyer,
      },
    ];

    if (isSecondBuyer.value) {
      buyers.push({
        key: "secondBuyer",
        label: "Buyer 2",
        data: requestForm.amlDetails.secondBuyer,
      });
    }

    return buyers;
  });

  const personAmlFields = computed(() => {
    const fields: any[] = [
      {
        key: "dateOfBirth",
        label: "Date of Birth",
        type: "date",
        width: "w-30",
        required: true,
      },
      {
        key: "nationality",
        label: "Nationality",
        type: "select",
        options: countryOptions,
        width: "w-30",
        required: true,
      },
      {
        key: "countryOfResidence",
        label: "Country of Residence",
        type: "select",
        options: countryOptions,
        width: "w-30",
        required: true,
      },
      {
        key: "occupation",
        label: "Occupation",
        type: "text",
        placeholder: "Occupation",
        width: "",
        required: true,
      },
      {
        key: "annualIncome",
        label: "Annual Income",
        type: "currency",
        min: 0,
        placeholder: "$300,000",
        width: "w-50",
        required: true,
      },
      {
        key: "sourceOfFunds",
        label: "Source of Funds",
        type: "text",
        placeholder: "E.g. Employment Income",
        width: "w-50",
        required: true,
      },
      {
        key: "sourceOfDepositFunds",
        otherKey: "sourceOfDepositFundsOther",
        label: "Source of Deposit Funds",
        type: "select",
        options: sourceOfDepositFundsOptions,
        width: "",
        required: true,
        hint: "Specifically the origin of the lump-sum deposit, separate from ongoing income.",
      },
    ];

    if (requestForm.purchaserType === "Sole Trader") {
      fields.push(
        {
          key: "abn",
          label: "ABN",
          type: "text",
          maxlength: 11,
          placeholder: "123 456 789 10",
          width: "w-50",
          required: true,
        },
        {
          key: "soleTraderBusinessName",
          label: "Sole Trader Business Name",
          type: "text",
          width: "w-50",
          required: true,
        },
        {
          key: "tradingName",
          label: "Trading Name (if different)",
          type: "text",
          width: "w-50",
          required: false,
        },
        {
          key: "dateBusinessStarted",
          label: "Date Business Started",
          type: "date",
          width: "w-50",
          required: true,
        },
        {
          key: "websiteUrl",
          label: "Website URL",
          type: "url",
          placeholder: "e.g. www.example.com.au",
          width: "",
          required: false,
        }
      );
    }

    return fields;
  });

  const trustSmsfTypeOptions = [
    "Discretionary (Family) Trust",
    "Unit Trust",
    "Fixed Trust",
    "Bare / Custodian Trust",
    "Testamentary Trust",
    "Self-Managed Super Fund (SMSF)",
    "Deceased Estate",
    "Other Trust (Please Specify)",
  ];

  const partnershipTypeOptions = [
    "Family / Limited Partnership",
    "Professional Partnership",
  ];

  const entityAmlFields = computed(() => {
    if (requestForm.purchaserType === "Trust / SMSF") {
      return [
        {
          key: "trustSmsfType",
          otherKey: "trustSmsfTypeOther",
          otherValue: "Other Trust (Please Specify)",
          label: "Trust / SMSF Type",
          type: "select",
          options: trustSmsfTypeOptions,
          width: "w-50",
          required: true,
        },
        {
          key: "abn",
          label: "ABN",
          type: "text",
          maxlength: 11,
          placeholder: "123 456 789 10",
          width: "w-50",
          required: false,
        },
        {
          key: "acn",
          label: "ACN",
          type: "text",
          maxlength: 9,
          placeholder: "123 456 789",
          width: "w-50",
          required: true,
        },
        {
          key: "trustName",
          label: "Trust Name",
          type: "text",
          width: "w-50",
          required: true,
        },
        {
          key: "tradingName",
          label: "Trading Name (if different)",
          type: "text",
          width: "w-50",
          required: false,
        },
        {
          key: "establishmentDate",
          label: "Establishment Date",
          type: "date",
          width: "w-50",
          required: true,
        },
      ];
    }

    if (requestForm.purchaserType === "Partnership") {
      return [
        {
          key: "partnershipType",
          label: "Partnership Type",
          type: "select",
          options: partnershipTypeOptions,
          width: "w-50",
          required: true,
        },
        {
          key: "abn",
          label: "ABN",
          type: "text",
          maxlength: 11,
          placeholder: "123 456 789 10",
          width: "w-50",
          required: true,
        },
        {
          key: "abnRegistrationDate",
          label: "ABN Registration Date",
          type: "date",
          width: "w-50",
          required: true,
        },
        {
          key: "registeredBusinessName",
          label: "Registered Business Name",
          type: "text",
          width: "w-50",
          required: true,
        },
        {
          key: "tradingName",
          label: "Trading Name (if different)",
          type: "text",
          width: "w-50",
          required: false,
        },
        {
          key: "websiteUrl",
          label: "Website URL",
          type: "url",
          placeholder: "e.g. www.example.com.au",
          width: "w-50",
          required: false,
        },
      ];
    }

    return [
      {
        key: "foreignCompany",
        label: "Is this a Foreign Company?",
        type: "boolean",
        width: "",
        required: true,
      },
      ...(requestForm.amlDetails.entity.foreignCompany === false
        ? [
            {
              key: "abn",
              label: "ABN",
              type: "text",
              maxlength: 11,
              placeholder: "123 456 789 10",
              width: "w-50",
              required: false,
            },
            {
              key: "acn",
              label: "ACN",
              type: "text",
              maxlength: 9,
              placeholder: "123 456 789",
              width: "w-50",
              required: true,
            },
          ]
        : []),
      {
        key: "registeredBusinessName",
        label: "Registered Business Name",
        type: "text",
        width: "w-50",
        required: true,
      },
      {
        key: "tradingName",
        label: "Trading Name (if different)",
        type: "text",
        width: "w-50",
        required: false,
      },
      {
        key: "incorporationDate",
        label: "Incorporation Date",
        type: "date",
        width: "w-50",
        required: true,
      },
      {
        key: "websiteUrl",
        label: "Website URL",
        type: "url",
        placeholder: "e.g. www.example.com.au",
        width: "w-50",
        required: false,
      },
    ];
  });

  const onToggleFirstBuyerMiddleName = (hasMiddleName: boolean) => {
    if (!hasMiddleName) {
      hasFirstBuyerMiddleName.value = false;
      requestForm.buyersData.firstBuyer.middleName = "";
      v$.value.buyersData.firstBuyer.middleName.$reset();
      return;
    }

    hasFirstBuyerMiddleName.value = true;
  };
  const onToggleSecondBuyerMiddleName = (hasMiddleName: boolean) => {
    if (!hasMiddleName) {
      hasSecondBuyerMiddleName.value = false;
      requestForm.buyersData.secondBuyer.middleName = "";
      v$.value.buyersData.secondBuyer.middleName.$reset();
      return;
    }

    hasSecondBuyerMiddleName.value = true;
  };

  const rules = computed(() => {
    const localRules = {
      purchaserType: {
        required: requiredIf(requestAmlInformation.value),
      },
      amlDetails: {
        firstBuyer: {},
        secondBuyer: {},
        entity: {},
      },
      buyerType: {
        required: requiredIf(
          getTemplateField(
            "default_group",
            "Buyer Type (owner occupier or investor)"
          )
        ),
      },
      buyersData: {
        firstBuyer: {
          firstName: { required },
          lastName: { required },
          email: { required, email },
          state: {
            required: requiredIf(
              getTemplateField(
                EoiTemplateGroups.BUYER_DETAILS,
                EoiTemplateFields.STATE
              )
            ),
          },
          suburb: {
            required: requiredIf(
              getTemplateField(
                EoiTemplateGroups.BUYER_DETAILS,
                EoiTemplateFields.SUBURB
              )
            ),
          },
          middleName: { required: requiredIf(hasFirstBuyerMiddleName.value) },
          phone: {
            required,
            validPhoneNumber: helpers.withMessage(
              "Value must be valid phone number",
              validPhoneNumber
            ),
          },
          postcode: { required, numeric, minLength: minLength(4) },
          address: { required },
          documents: [
            {
              name: {},
              link: {},
              documentType: {},
            },
          ],
        },
        secondBuyer: {
          firstName: {},
          middleName: {},
          lastName: {},
          address: {},
          email: {},
          phone: {},
          state: {},
          suburb: {},
          postcode: {},
          documents: [
            {
              name: {},
              link: {},
              documentType: {},
            },
          ],
        },
      },
      companyDetails: {
        name: {},
        businessIdentifier: {},
        address: {},
        state: {},
        postcode: {},
        suburb: {},
      },
      conveyancerDetails: {
        firmName: { required },
        address: { required },
        postcode: {},
        state: {},
        suburb: {},
        contactPerson: {
          fullName: { required },
          email: { required, email },
          phone: {
            required,
            validPhoneNumber: helpers.withMessage(
              "Value must be valid phone number",
              validPhoneNumber
            ),
          },
        },
      },
      contractConditions: {
        subjectToFinance: {},
        financeStatus: {},
        landLoanAmount: {},
        FIRB: {},
        otherConditions: {},
        bankApprovalDocument: {
          name: {},
          link: {},
        },
        financeBrokerDetails: {
          email: {},
          phone: {},
        },
      },
    };

    if (
      requestAmlInformation.value &&
      ["Individual", "Sole Trader"].includes(requestForm.purchaserType)
    ) {
      const getPersonRules = (buyer: Record<string, string>) => ({
        dateOfBirth: { required },
        nationality: { required },
        countryOfResidence: { required },
        occupation: { required },
        annualIncome: { required, numeric, minValue: minValue(0) },
        sourceOfFunds: { required },
        sourceOfDepositFunds: { required },
        sourceOfDepositFundsOther: {
          required: requiredIf(
            buyer.sourceOfDepositFunds === "Other (Please Specify)"
          ),
        },
      });
      const personRules: Record<string, any> = getPersonRules(
        requestForm.amlDetails.firstBuyer
      );

      if (requestForm.purchaserType === "Sole Trader") {
        Object.assign(personRules, {
          abn: { required, numeric, minLength: minLength(11) },
          soleTraderBusinessName: { required },
          dateBusinessStarted: { required },
        });
      }

      localRules.amlDetails.firstBuyer = personRules;

      if (isSecondBuyer.value) {
        const secondBuyerRules: Record<string, any> = getPersonRules(
          requestForm.amlDetails.secondBuyer
        );

        if (requestForm.purchaserType === "Sole Trader") {
          Object.assign(secondBuyerRules, {
            abn: { required, numeric, minLength: minLength(11) },
            soleTraderBusinessName: { required },
            dateBusinessStarted: { required },
          });
        }

        localRules.amlDetails.secondBuyer = secondBuyerRules;
      }
    }

    if (
      requestAmlInformation.value &&
      requestForm.purchaserType === "Company"
    ) {
      localRules.amlDetails.entity = {
        foreignCompany: { required },
        registeredBusinessName: { required },
        incorporationDate: { required },
        ...(requestForm.amlDetails.entity.foreignCompany === false
          ? {
              abn: { numeric, minLength: minLength(11) },
              acn: { required, numeric, minLength: minLength(9) },
            }
          : {}),
      };
    }

    if (
      requestAmlInformation.value &&
      requestForm.purchaserType === "Trust / SMSF"
    ) {
      localRules.amlDetails.entity = {
        trustSmsfType: { required },
        trustSmsfTypeOther: {
          required: requiredIf(
            requestForm.amlDetails.entity.trustSmsfType ===
              "Other Trust (Please Specify)"
          ),
        },
        abn: { numeric, minLength: minLength(11) },
        acn: { required, numeric, minLength: minLength(9) },
        trustName: { required },
        establishmentDate: { required },
      };
    }

    if (
      requestAmlInformation.value &&
      requestForm.purchaserType === "Partnership"
    ) {
      localRules.amlDetails.entity = {
        partnershipType: { required },
        abn: { required, numeric, minLength: minLength(11) },
        abnRegistrationDate: { required },
        registeredBusinessName: { required },
      };
    }

    if (isPurchasingByCompany.value) {
      localRules.companyDetails = {
        name: {
          required: requiredIf(
            getTemplateField(
              EoiTemplateGroups.PURCHASE_AS_COMPANY_DETAILS,
              EoiTemplateFields.NAME
            )
          ),
        },
        businessIdentifier: {
          required: requiredIf(
            getTemplateField(
              EoiTemplateGroups.PURCHASE_AS_COMPANY_DETAILS,
              EoiTemplateFields.ABN_ACN
            )
          ),
        },
        address: {
          required: requiredIf(
            getTemplateField(
              EoiTemplateGroups.PURCHASE_AS_COMPANY_DETAILS,
              EoiTemplateFields.REGISTERED_ADDRESS
            )
          ),
        },
        state: {
          required: requiredIf(
            getTemplateField(
              EoiTemplateGroups.PURCHASE_AS_COMPANY_DETAILS,
              EoiTemplateFields.STATE
            )
          ),
        },
        postcode: {
          required: requiredIf(
            getTemplateField(
              EoiTemplateGroups.PURCHASE_AS_COMPANY_DETAILS,
              EoiTemplateFields.POSTCODE
            )
          ),
          numeric,
          minLength: minLength(4),
        },
        suburb: {
          required: requiredIf(
            getTemplateField(
              EoiTemplateGroups.PURCHASE_AS_COMPANY_DETAILS,
              EoiTemplateFields.SUBURB
            )
          ),
        },
      };
    }

    if (isSecondBuyer.value) {
      localRules.buyersData.secondBuyer = {
        firstName: { required },
        middleName: { required: requiredIf(hasFirstBuyerMiddleName.value) },
        lastName: { required },
        email: { required, email },
        postcode: {
          required,
          numeric,
          minLength: minLength(4),
        },
        state: {
          required,
        },
        suburb: {
          required,
        },
        address: { required },
        phone: {
          required,
          validPhoneNumber: helpers.withMessage(
            "Value must be valid phone number",
            validPhoneNumber
          ),
        },
        documents: [
          {
            name: { required },
            link: { required },
            documentType: { required },
          },
        ],
      };
    }

    if (
      requestForm.contractConditions.subjectToFinance &&
      requestForm.contractConditions.financeStatus === "Pre-approved"
    ) {
      localRules.contractConditions.bankApprovalDocument = {
        name: { required },
        link: { required },
      };
    }

    if (
      requestForm.contractConditions.subjectToFinance &&
      getTemplateField(
        EoiTemplateGroups.CONTRACT_CONDITIONS,
        EoiTemplateFields.LAND_LOAN_AMOUNT
      )
    ) {
      localRules.contractConditions.landLoanAmount = {
        required,
        numeric,
        minValue: minValue(1000),
      };
    }

    localRules.contractConditions.financeBrokerDetails =
      getFinanceBrokerDetailsRules.value;

    if (subjectToAny.value) {
      localRules.contractConditions.otherConditions = { required };
    }

    if (Object.keys(eoiTemplate.value).length > 0) {
      localRules.buyersData.firstBuyer.documents = [
        {
          name: {
            required: requiredIf(
              getTemplateField(
                "Buyer details",
                "Upload ID (Drivers license, Passport)"
              )
            ),
          },
          link: {
            required: requiredIf(
              getTemplateField(
                "Buyer details",
                "Upload ID (Drivers license, Passport)"
              )
            ),
          },
          documentType: {
            required: requiredIf(
              getTemplateField(
                "Buyer details",
                "Upload ID (Drivers license, Passport)"
              )
            ),
          },
        },
      ];

      if (isSecondBuyer.value) {
        localRules.buyersData.secondBuyer = {
          firstName: {
            required: requiredIf(
              getTemplateField("Buyer 2 details", "First Name")
            ),
          },
          postcode: {
            required: requiredIf(
              getTemplateField(
                EoiTemplateGroups.BUYER_DETAILS_2,
                EoiTemplateFields.POSTCODE
              )
            ),
            numeric,
            minLength: minLength(4),
          },
          state: {
            required: requiredIf(
              getTemplateField(
                EoiTemplateGroups.BUYER_DETAILS_2,
                EoiTemplateFields.STATE
              )
            ),
          },
          suburb: {
            required: requiredIf(
              getTemplateField(
                EoiTemplateGroups.BUYER_DETAILS_2,
                EoiTemplateFields.SUBURB
              )
            ),
          },
          middleName: {
            required: requiredIf(
              getTemplateField("Buyer 2 details", "Middle Name(s)") &&
                hasSecondBuyerMiddleName.value
            ),
          },
          lastName: {
            required: requiredIf(
              getTemplateField("Buyer 2 details", "Last Name")
            ),
          },
          address: {
            required: requiredIf(
              getTemplateField("Buyer 2 details", "Address")
            ),
          },
          email: {
            required: requiredIf(getTemplateField("Buyer 2 details", "Email")),
            email,
          },
          phone: {
            required,
            validPhoneNumber: helpers.withMessage(
              "Value must be valid phone number",
              validPhoneNumber
            ),
          },
          documents: [
            {
              name: {
                required: requiredIf(
                  getTemplateField(
                    "Buyer 2 details",
                    "Upload ID (Drivers license, Passport)"
                  )
                ),
              },
              link: {
                required: requiredIf(
                  getTemplateField(
                    "Buyer 2 details",
                    "Upload ID (Drivers license, Passport)"
                  )
                ),
              },
              documentType: {
                required: requiredIf(
                  getTemplateField(
                    "Buyer 2 details",
                    "Upload ID (Drivers license, Passport)"
                  )
                ),
              },
            },
          ],
        };

        if (!getTemplateField("Buyer 2 details", "Phone Number")) {
          localRules.buyersData.secondBuyer.phone = {};
        }
      }

      localRules.conveyancerDetails = {
        firmName: {
          required: requiredIf(
            getTemplateField("Solicitor Details", "Firm Name")
          ),
        },
        address: {
          required: requiredIf(
            getTemplateField("Solicitor Details", "Address")
          ),
        },
        postcode: {
          required: requiredIf(
            getTemplateField("Solicitor Details", "Postcode")
          ),
        },
        suburb: {
          required: requiredIf(getTemplateField("Solicitor Details", "Suburb")),
        },
        state: {
          required: requiredIf(getTemplateField("Solicitor Details", "State")),
        },
        contactPerson: {
          fullName: {
            required: requiredIf(
              getTemplateField("Solicitor Details", "Contact Person")
            ),
          },
          email: {
            required: requiredIf(
              getTemplateField("Solicitor Details", "Email")
            ),
            email,
          },
          phone: {
            required,
            validPhoneNumber: helpers.withMessage(
              "Value must be valid phone number",
              validPhoneNumber
            ),
          },
        },
      };

      if (!getTemplateField("Solicitor Details", "Phone Number")) {
        localRules.conveyancerDetails.contactPerson.phone = {} as any;
      }
    }

    return localRules;
  });

  const getFinanceBrokerDetailsRules = computed(() => {
    if (
      requestForm.contractConditions.subjectToFinance &&
      getTemplateField("Contract Conditions", "Finance broker details")
    ) {
      return {
        email: { email },
        phone: {
          validPhoneNumber: requestForm.contractConditions.financeBrokerDetails
            .phone
            ? helpers.withMessage(
                "Value must be valid phone number",
                validPhoneNumber
              )
            : true,
        },
      };
    }

    return {
      email: {},
      phone: {},
    };
  });

  const v$ = useVuelidate(rules, requestForm);

  const setPurchaserType = (purchaserType: string) => {
    requestForm.purchaserType = purchaserType;
    purchaserTypeError.value = false;
    v$.value.purchaserType.$reset();
  };

  const setAmlField = (
    group: string,
    field: string,
    value: string | boolean
  ) => {
    requestForm.amlDetails[group][field] = value;
    touchAmlField(group, field);
  };

  const formatAmlCurrency = (value: unknown) => {
    const digits = String(value ?? "").replace(/\D/g, "");

    return digits ? formatPrice(digits) : "";
  };

  const parseAmlCurrency = (value: unknown): number | "" => {
    const digits = String(value ?? "").replace(/\D/g, "");

    return digits ? Number(digits) : "";
  };

  const setAmlCurrencyField = (group: string, field: string, event: Event) => {
    const input = event.target as HTMLInputElement;
    const value = parseAmlCurrency(input.value);

    requestForm.amlDetails[group][field] = value;
    input.value = value === "" ? "" : formatPrice(value);
  };

  const getAmlFieldRows = (fields: Array<Record<string, unknown>>) => {
    const rows: Array<Array<Record<string, unknown>>> = [];
    let row: Array<Record<string, unknown>> = [];

    fields.forEach((field) => {
      if (!field.width) {
        if (row.length) rows.push(row);

        row = [];
        rows.push([field]);
        return;
      }

      row.push(field);

      if (
        (field.width === "w-30" && row.length === 3) ||
        (field.width !== "w-30" && row.length === 2)
      ) {
        rows.push(row);
        row = [];
      }
    });

    if (row.length) rows.push(row);

    return rows;
  };

  const getSelectedOtherFields = (
    values: Record<string, string | boolean>,
    fields: Array<Record<string, any>>
  ) =>
    fields.filter(
      (field) =>
        field.otherKey &&
        values[field.key] === (field.otherValue || "Other (Please Specify)")
    );

  const getAmlFieldValue = (group: string, field: Record<string, any>) => {
    const value = requestForm.amlDetails[group][field.key];

    if (field.type === "boolean") {
      return value === null ? "Select" : value ? "Yes" : "No";
    }

    return value || field.placeholder || "Select";
  };

  const touchAmlField = (group: string, field: string) => {
    v$.value.amlDetails?.[group]?.[field]?.$touch();
  };

  const isAmlFieldInvalid = (group: string, field: string) =>
    Boolean(v$.value.amlDetails?.[group]?.[field]?.$error);

  onBeforeMount(() => {
    formLoading.value = true;
    store
      .dispatch("eoi/getCustomTemplate", props.estateId)
      .then((res) => {
        if (props.eoiTemplate) {
          eoiTemplate.value = props.eoiTemplate;
          return eoiTemplate.value;
        } else if (res.data) {
          eoiTemplate.value = res.data;
          return res.data;
        } else {
          return store.dispatch("eoi/getDefaultTemplate").then((response) => {
            eoiTemplate.value = response.data;
          });
        }
      })
      .finally(() => {
        formLoading.value = false;
        const eoiUserData = LocalStorageService.get("eoiUserData");

        if (!isLogged() && eoiUserData) {
          updateUserProfileFromEoi(JSON.parse(eoiUserData));
        }
      });
  });
  onMounted(async () => {
    await store
      .dispatch("estate/getEstate", {
        id: props.estateId,
        businessId: props.businessId,
      })
      .then((res) => {
        estate.value = res.data;

        if (!props.isPreview) {
          changePrimaryColorEoiViews(res.data, props.businessId || null);
        }
      });
    const lotAction = isLogged() ? "getLot" : "getOpenLot";

    if (props.isPreview) {
      return;
    }

    store.dispatch(`lot/${lotAction}`, props.lotId).then((res) => {
      const allowedLotStatus = [LotStatus.AVAILABLE, LotStatus.HOLD];

      if (!allowedLotStatus.includes(res.data.status)) {
        toast.error("This lot is not available. Please choose another lot", {
          position: POSITION.TOP_LEFT,
        });
        router.replace({
          name: "EstateMap",
          params: { estateId: estate.value._id },
        });
      } else {
        lot.value = res.data;
      }
    });
  });

  onUnmounted(() => {
    resetPrimaryColor();
  });

  const hasActiveFields = (groupName: string) => {
    return (
      getEoiTemplateGroups?.value
        .filter((group: any) => group.groupName === groupName)[0]
        .fields.filter((field: any) => field.isActive === true).length > 0
    );
  };
  const onBack = () => {
    if (props.isPreview) {
      return;
    }

    showConfirmationModal.value = false;
    const { backUrl } = router.currentRoute.value.params;

    transitionName.value = "";

    if (backUrl) {
      router.push(backUrl as string);
    } else {
      router.push(
        `/estate/${props.estateId}?stageId=${
          !props.stageId ? "" : props.stageId
        }`
      );
    }
  };

  const getUserProfile = () => {
    if (isLogged() && isCustomer.value) {
      store.dispatch("user/getUser", customerId.value).then((res) => {
        userProfile.value = res.data;
        requestForm.buyersData.firstBuyer.firstName = res.data.firstName;
        requestForm.buyersData.firstBuyer.middleName = res.data.middleName;
        requestForm.buyersData.firstBuyer.lastName = res.data.lastName;
        requestForm.buyersData.firstBuyer.email = res.data.email;
        requestForm.buyersData.firstBuyer.phone = res.data.phone;
        requestForm.buyersData.firstBuyer.address = res.data.detailsId.address;
        requestForm.buyersData.firstBuyer.postcode = res.data.detailsId.postcode
          ? res.data.detailsId.postcode
          : res.data.detailsId.state;
      });
    }
  };

  const setBuyerTypeField = (buyerType: string) => {
    if (
      buyerType &&
      getTemplateField(
        "default_group",
        "Buyer Type (owner occupier or investor)"
      )
    ) {
      requestForm.buyerType = buyerType;
    }
  };

  const setDefaultDocumentsFieldsFirstBuyer = (documents: Documents[]) => {
    if (
      !documents.length &&
      getTemplateField("Buyer details", "Upload ID (Drivers license, Passport)")
    ) {
      const data = {
        name: "",
        link: "",
        documentType: "Drivers license",
        modifiedDate: "",
      };

      requestForm.buyersData.firstBuyer.documents.push(data);
    }
  };

  const setConveyancerDetails = (conveyancerDetails: Conveyancer) => {
    if (conveyancerDetails) {
      requestForm.conveyancerDetails = conveyancerDetails;
    }
  };

  const setContractConditions = (contractConditions: ContractConditions) => {
    if (contractConditions && hasActiveFields("Contract Conditions")) {
      requestForm.contractConditions = contractConditions;
    }

    if (!contractConditions?.financeBrokerDetails) {
      requestForm.contractConditions.financeBrokerDetails = {
        lendingInstitution: "",
        brokerName: "",
        email: "",
        phone: "",
      };
    }
  };

  const updateUserProfileFromEoi = (data: ResponseEoiDetails) => {
    setBuyerTypeField(data?.buyerType);
    requestForm.purchaserType = data?.purchaserType || "";

    if (data?.amlDetails) {
      requestForm.amlDetails = {
        ...requestForm.amlDetails,
        ...data.amlDetails,
        firstBuyer: {
          ...requestForm.amlDetails.firstBuyer,
          ...data.amlDetails.firstBuyer,
        },
        secondBuyer: {
          ...requestForm.amlDetails.secondBuyer,
          ...data.amlDetails.secondBuyer,
        },
        entity: {
          ...requestForm.amlDetails.entity,
          ...data.amlDetails.entity,
        },
      };
    }

    requestForm.buyersData.firstBuyer = data.buyersData.firstBuyer;
    setDefaultDocumentsFieldsFirstBuyer(data.buyersData.firstBuyer?.documents);
    setConveyancerDetails(data?.conveyancerDetails);
    setContractConditions(data?.contractConditions);
    isZeroDeposit.value = data.isZeroDeposit;

    if (requestForm.contractConditions.otherConditions) {
      subjectToAny.value = true;
    }

    requestForm.contractConditions.financeStatus = !data.contractConditions
      ?.financeStatus
      ? "Pending"
      : data.contractConditions.financeStatus;

    if (!("secondBuyer" in data.buyersData)) {
      requestForm.buyersData.secondBuyer = {
        firstName: "",
        middleName: "",
        lastName: "",
        email: "",
        phone: "",
        documents: [
          {
            name: "",
            link: "",
            documentType: "Drivers license",
            modifiedDate: "",
          },
        ],
      };
    } else {
      requestForm.buyersData.secondBuyer = data.buyersData.secondBuyer;
      isSecondBuyer.value = true;
    }
  };

  if (!eoiId.value && !props.isPreview) {
    if (isLogged() && isCustomer.value) {
      store
        .dispatch("eoi/createEoi", {
          customerId: customerId.value,
          estateId: props.estateId,
          lotId: props.lotId,
          ...(props.businessId ? { businessId: props.businessId } : {}),
        })
        .then((res) => {
          eoiId.value = res.data._id;
          isZeroDeposit.value = res.data.isZeroDeposit;
          getUserProfile();
          isCreateEioError.value = false;
          emits("update:isCreateEoiError", false);
        })
        .catch(() => {
          if (!isCustomer.value) return;

          isCreateEioError.value = true;
          emits("update:isCreateEoiError", true);
        });
    } else {
      store
        .dispatch("eoi/createUnregisteredCustomer", {
          estateId: props.estateId,
          lotId: props.lotId,
          ...(props.businessId ? { businessId: props.businessId } : {}),
        })
        .then((res) => {
          if (!isLogged() || (isLogged() && !isCustomer.value)) {
            customerId.value = res.data.customerId;
          }

          eoiId.value = res.data._id;
          isZeroDeposit.value = res.data.isZeroDeposit;
          getUserProfile();
          isCreateEioError.value = false;
          emits("update:isCreateEoiError", false);
        })
        .catch(() => {
          isCreateEioError.value = true;
          emits("update:isCreateEoiError", true);
        });
    }
  } else {
    if (isLogged() && !props.isPreview) {
      store.dispatch("eoi/getEoiById", eoiId.value).then((res) => {
        updateUserProfileFromEoi(res.data);
        getUserProfile();
      });
    }
  }

  if (
    !router.currentRoute.value.query?.eoiId &&
    isLogged() &&
    !props.isPreview
  ) {
    store
      .dispatch("eoi/getEois", {
        customerId: customerId.value,
        estateId: props.estateId,
        lotId: props.lotId,
      })
      .then((res) => {
        const activeRequest = res.data.filter(
          (item) =>
            item.status != EoiStatus.TEMP && item.status != EoiStatus.DECLINED
        );

        if (activeRequest.length != 0) {
          showConfirmationModal.value = true;
        }
      });
  }

  const updateUserProfileDetails = () => {
    const profileDetails = userProfile.value?.detailsId;

    if (!profileDetails?._id) {
      return Promise.resolve();
    }

    const updateUserProfileDetails = {
      id: "",
      data: {},
    };

    if (
      requestForm.buyersData.firstBuyer.address &&
      profileDetails.address != requestForm.buyersData.firstBuyer.address
    ) {
      updateUserProfileDetails.data.address =
        requestForm.buyersData.firstBuyer.address;
    }

    if (
      requestForm.buyersData.firstBuyer.postcode &&
      (profileDetails.postcode || profileDetails.state) !==
        requestForm.buyersData.firstBuyer.postcode
    ) {
      updateUserProfileDetails.data.postcode =
        requestForm.buyersData.firstBuyer.postcode;
    }

    if (Object.keys(updateUserProfileDetails.data).length > 0) {
      updateUserProfileDetails.id = profileDetails._id;
      return store.dispatch(
        "customer/updateCustomerDetails",
        updateUserProfileDetails
      );
    }

    return Promise.resolve();
  };

  const updateUserProfile = async () => {
    const updateUserProfileData = {
      id: "",
      data: {},
    };
    const profileUpdates: Array<Promise<unknown>> = [];

    if (
      requestForm.buyersData.firstBuyer.firstName &&
      userProfile.value.firstName != requestForm.buyersData.firstBuyer.firstName
    ) {
      updateUserProfileData.data.firstName =
        requestForm.buyersData.firstBuyer.firstName;
    }

    if (
      requestForm.buyersData.firstBuyer.middleName &&
      userProfile.value.middleName !=
        requestForm.buyersData.firstBuyer.middleName
    ) {
      updateUserProfileData.data.middleName =
        requestForm.buyersData.firstBuyer.middleName;
    }

    if (
      requestForm.buyersData.firstBuyer.lastName &&
      userProfile.value.lastName != requestForm.buyersData.firstBuyer.lastName
    ) {
      updateUserProfileData.data.lastName =
        requestForm.buyersData.firstBuyer.lastName;
    }

    if (
      requestForm.buyersData.firstBuyer.phone &&
      userProfile.value.phone != requestForm.buyersData.firstBuyer.phone
    ) {
      updateUserProfileData.data.phone =
        requestForm.buyersData.firstBuyer.phone;
    }

    if (Object.keys(updateUserProfileData.data).length > 0) {
      updateUserProfileData.id = customerId.value;
      profileUpdates.push(
        store.dispatch("user/updateUser", updateUserProfileData)
      );
    }

    profileUpdates.push(updateUserProfileDetails());
    await Promise.all(profileUpdates);
  };

  const addFileToDropDown = (owner: string) => {
    documentList[owner] =
      buyerDriverList.value.length > 0
        ? buyerDriverList.value
        : buyerPassportList.value;
    setFileByIndex(owner, 0);
  };

  const setFileByIndex = (owner: string, ind: number) => {
    const coreObj =
      owner === "bank"
        ? requestForm.contractConditions.bankApprovalDocument
        : requestForm.buyersData[owner].documents[0];

    if (coreObj) {
      coreObj.name = documentList[owner][ind]?.name;
      coreObj.link = documentList[owner][ind].link;
      coreObj.documentType = documentList[owner][ind].documentType;
    }
  };

  const searchFileInUploaded = (owner: string) => {
    if (requestForm.buyersData[owner].documents[0]?.name) {
      documentList[owner] =
        requestForm.buyersData[owner].documents[0].documentType === "Passport"
          ? buyerPassportList.value
          : buyerDriverList.value;
      const indexOfUploadedFile = documentList[owner].findIndex(
        (item) => item.name === requestForm.buyersData[owner].documents[0].name
      );

      if (indexOfUploadedFile != -1) {
        setFileByIndex(owner, indexOfUploadedFile);
      }
    } else {
      if (
        buyerPassportList.value.length != 0 ||
        buyerDriverList.value.length != 0
      ) {
        addFileToDropDown(owner);
      }
    }
  };
  const formatLandLoanAmountOptions = {
    numeral: true,
    numeralThousandsGroupStyle: "thousand",
    numeralPositiveOnly: true,
  };

  function setBuyerIdType(owner: string, value: string) {
    requestForm.buyersData[owner].documents[0].documentType = value;

    documentList[owner] =
      value === "Passport" ? buyerPassportList.value : buyerDriverList.value;

    if (documentList[owner].length > 0) {
      setFileByIndex(owner, 0);
    }
  }

  function subjectToFinance(value: boolean) {
    requestForm.contractConditions.subjectToFinance = value;

    if (!value) {
      setFinanceStatus("bank");
    }
  }

  const setFinanceStatus = (
    owner: string,
    financeStatus = "Pending",
    index = 0
  ) => {
    requestForm.contractConditions.financeStatus = financeStatus;

    if (documentList[owner].length > 0 && financeStatus === "Pre-approved") {
      setFileByIndex(owner, index);
    } else {
      if (
        financeStatus === "Pending" &&
        requestForm?.contractConditions?.bankApprovalDocument.name
      ) {
        deleteDocument(owner);
      }

      if (
        owner === "bank" &&
        requestForm?.contractConditions?.bankApprovalDocument
      ) {
        requestForm.contractConditions.bankApprovalDocument.documentType = "";
      }
    }
  };

  const setBuyerState = (buyer: string, value: string) => {
    requestForm.buyersData[buyer].postcode = value;
  };

  function clearFileInfo(owner: string) {
    fileInfo[owner].isError = false;
    fileInfo[owner].errorMessage = "";
  }

  function addErrorToFileInfo(
    fileOwner: string,
    errorMessage: string,
    fileName: string
  ) {
    fileInfo[fileOwner].isError = true;
    fileInfo[fileOwner].errorMessage = errorMessage;
    const coreObj =
      fileOwner === "bank"
        ? requestForm.contractConditions.bankApprovalDocument
        : requestForm.buyersData[fileOwner].documents[0];

    coreObj.name = fileName;
  }

  const isFileLoading = ref(false);
  const uploadFile = (event: Event, fileOwner: string) => {
    const target = event?.target as HTMLInputElement;

    if (!target?.files || target.files.length === 0) {
      return;
    }

    if (target.files[0].size > MaxFileSize) {
      addErrorToFileInfo(fileOwner, fileSizeError, target.files[0].name);
      return;
    }

    if (
      !allowedDocumentMimeTypes.includes(target.files[0].type) &&
      !target.files[0].name.toLowerCase().endsWith(".heic")
    ) {
      addErrorToFileInfo(fileOwner, fileTypeError, target.files[0].name);
      return;
    }

    clearFileInfo(fileOwner);

    if (eoiId.value) {
      isFileLoading.value = true;
      const data = new FormData();

      data.append("file", event.target.files[0]);
      const action =
        isLogged() && isCustomer.value
          ? "uploadDocument"
          : "uploadDocumentForUnregisteredCustomer";

      store
        .dispatch(`eoi/${action}`, { data, id: eoiId.value })
        .then((res) => {
          const coreObj =
            fileOwner === "bank"
              ? requestForm.contractConditions.bankApprovalDocument
              : requestForm.buyersData[fileOwner].documents[0];

          coreObj.name = res.data.filename;
          coreObj.link = res.data.link;
          coreObj.modifiedDate = res.data.modifiedDate;
          requestForm.contractConditions.bankApprovalDocument.documentType =
            fileOwner === "bank" ? "Bank approval" : "";
        })
        .finally(() => (isFileLoading.value = false));
    }
  };

  const deleteDocument = (owner: string) => {
    const coreObj =
      owner === "bank"
        ? requestForm.contractConditions.bankApprovalDocument
        : requestForm.buyersData[owner].documents[0];
    const action =
      isLogged() && isCustomer.value
        ? "deleteDocument"
        : "deleteDocumentForUnregisteredCustomer";

    store
      .dispatch(`eoi/${action}`, {
        id: eoiId.value,
        fileName: coreObj.name,
      })
      .then(() => {
        if (coreObj) {
          coreObj.name = "";
          coreObj.link = "";
          coreObj.modifiedDate = "";
        }

        clearFileInfo(owner);
      });
  };

  function changeSelectFile(owner: string, value: any) {
    const coreObj =
      owner === "bank"
        ? requestForm.contractConditions.bankApprovalDocument
        : requestForm.buyersData[owner].documents[0];

    coreObj.name = value.name;
    coreObj.link = value.link;
    coreObj.modifiedDate = value.modifiedDate;
    clearFileInfo(owner);
  }

  const prepareEoiData = () => {
    updatedEoiFormObject.value = {};
    updatedEoiFormObject.value.data = JSON.parse(JSON.stringify(requestForm));
    updatedEoiFormObject.value.id = eoiId.value;
    updatedEoiFormObject.value.data.contractConditions.financeStatus =
      requestForm.contractConditions.subjectToFinance
        ? requestForm.contractConditions.financeStatus
        : "";
    updatedEoiFormObject.value.data.contractConditions.otherConditions =
      subjectToAny.value ? requestForm.contractConditions.otherConditions : "";
    updatedEoiFormObject.value.data.contractConditions.bankApprovalDocument.documentType =
      updatedEoiFormObject.value.data.contractConditions.financeStatus ===
      "Pre-approved"
        ? requestForm.contractConditions.bankApprovalDocument.documentType
        : "";

    if (!isSecondBuyer.value) {
      delete updatedEoiFormObject.value.data.buyersData.secondBuyer;
    }

    updateFirstBuyerDetails();
    updateSecondBuyerDetails();
    updateDefaultGroup();
    updateContractConditions();
    updateSolicitorDetails();
    updateCompanyDetails();
    updateAmlDetails();
    return updatedEoiFormObject.value;
  };

  const updateAmlDetails = () => {
    const data = updatedEoiFormObject.value.data;

    if (!requestAmlInformation.value || !data.purchaserType) {
      delete data.purchaserType;
      delete data.amlDetails;
      return;
    }

    if (
      ["Company", "Trust / SMSF", "Partnership"].includes(data.purchaserType)
    ) {
      const entityKeys = entityAmlFields.value.flatMap((field) =>
        "otherKey" in field && field.otherKey
          ? [field.key, field.otherKey]
          : [field.key]
      );

      data.amlDetails = {
        entity: Object.fromEntries(
          entityKeys.map((key) => [key, data.amlDetails.entity[key]])
        ),
      };
      return;
    }

    const personKeys = personAmlFields.value.flatMap((field) =>
      "otherKey" in field && field.otherKey
        ? [field.key, field.otherKey]
        : [field.key]
    );
    const personAmlDetails = data.amlDetails;

    data.amlDetails = {
      firstBuyer: Object.fromEntries(
        personKeys.map((key) => [
          key,
          key === "annualIncome"
            ? parseAmlCurrency(personAmlDetails.firstBuyer[key])
            : personAmlDetails.firstBuyer[key],
        ])
      ),
    };

    if (isSecondBuyer.value) {
      data.amlDetails.secondBuyer = Object.fromEntries(
        personKeys.map((key) => [
          key,
          key === "annualIncome"
            ? parseAmlCurrency(personAmlDetails.secondBuyer[key])
            : personAmlDetails.secondBuyer[key],
        ])
      );
    }
  };

  const updateFirstBuyerDetails = () => {
    if (
      !getTemplateField(
        "Buyer details",
        "Upload ID (Drivers license, Passport)"
      )
    ) {
      updatedEoiFormObject.value.data.buyersData.firstBuyer.documents = [];
    }
  };

  const updateSecondBuyerDetails = () => {
    if (isSecondBuyer.value && hasActiveFields("Buyer 2 details")) {
      if (!getTemplateField("Buyer 2 details", "First Name")) {
        delete updatedEoiFormObject.value.data.buyersData.secondBuyer.firstName;
      }

      if (!getTemplateField("Buyer 2 details", "Last Name")) {
        delete updatedEoiFormObject.value.data.buyersData.secondBuyer.lastName;
      }

      if (!getTemplateField("Buyer 2 details", "Middle Name(s)")) {
        delete updatedEoiFormObject.value.data.buyersData.secondBuyer
          .middleName;
      }

      if (!getTemplateField("Buyer 2 details", "Address")) {
        delete updatedEoiFormObject.value.data.buyersData.secondBuyer.address;
      }

      if (!getTemplateField("Buyer 2 details", "Email")) {
        delete updatedEoiFormObject.value.data.buyersData.secondBuyer.email;
      }

      if (!getTemplateField("Buyer 2 details", "Phone Number")) {
        delete updatedEoiFormObject.value.data.buyersData.secondBuyer.phone;
      }

      if (!getTemplateField("Buyer 2 details", "Suburb")) {
        delete updatedEoiFormObject.value.data.buyersData.secondBuyer.suburb;
      }

      if (!getTemplateField("Buyer 2 details", "State")) {
        delete updatedEoiFormObject.value.data.buyersData.secondBuyer.state;
      }

      if (!getTemplateField("Buyer 2 details", "Postcode")) {
        delete updatedEoiFormObject.value.data.buyersData.secondBuyer.postcode;
      }

      if (
        !getTemplateField(
          "Buyer 2 details",
          "Upload ID (Drivers license, Passport)"
        )
      ) {
        updatedEoiFormObject.value.data.buyersData.secondBuyer.documents = [];
      }
    }
  };

  const updateDefaultGroup = () => {
    if (
      !getTemplateField(
        "default_group",
        "Buyer Type (owner occupier or investor)"
      )
    ) {
      delete updatedEoiFormObject.value.data.buyerType;
    }

    if (!getTemplateField("default_group", "Agent Referrer (if applicable)")) {
      delete updatedEoiFormObject.value.data.agentReferrer;
    }
  };

  const updateContractConditions = () => {
    if (!hasActiveFields("Contract Conditions")) {
      delete updatedEoiFormObject.value.data.contractConditions;
    } else {
      if (
        !getTemplateField("Contract Conditions", "Contract subject to finance?")
      ) {
        delete updatedEoiFormObject.value.data.contractConditions
          .subjectToFinance;
      }

      if (
        !getTemplateField(
          "Contract Conditions",
          "Contract subject to any other conditions?"
        )
      ) {
        delete updatedEoiFormObject.value.data.contractConditions
          .otherConditions;
      }

      if (
        !getTemplateField("Contract Conditions", "Contract subject to FIRB?")
      ) {
        delete updatedEoiFormObject.value.data.contractConditions.FIRB;
      }

      if (
        !updatedEoiFormObject.value?.data?.contractConditions
          ?.subjectToFinance ||
        !getTemplateField("Contract Conditions", "Finance broker details")
      ) {
        delete updatedEoiFormObject.value.data.contractConditions
          .financeBrokerDetails;
      }
    }
  };

  const updateSolicitorDetails = () => {
    if (!hasActiveFields("Solicitor Details")) {
      delete updatedEoiFormObject.value.data.conveyancerDetails;
    } else if (haveOwnSolicitor.value) {
      if (!getTemplateField("Solicitor Details", "Firm Name")) {
        delete updatedEoiFormObject.value.data.conveyancerDetails.firmName;
      }

      if (!getTemplateField("Solicitor Details", "Contact Person")) {
        delete updatedEoiFormObject.value.data.conveyancerDetails.contactPerson
          .fullName;
      }

      if (!getTemplateField("Solicitor Details", "Address")) {
        delete updatedEoiFormObject.value.data.conveyancerDetails.address;
      }

      if (!getTemplateField("Solicitor Details", "Suburb")) {
        delete updatedEoiFormObject.value.data.conveyancerDetails.suburb;
      }

      if (!getTemplateField("Solicitor Details", "State")) {
        delete updatedEoiFormObject.value.data.conveyancerDetails.state;
      }

      if (!getTemplateField("Solicitor Details", "Postcode")) {
        delete updatedEoiFormObject.value.data.conveyancerDetails.postcode;
      }

      if (!getTemplateField("Solicitor Details", "Email")) {
        delete updatedEoiFormObject.value.data.conveyancerDetails.contactPerson
          .email;
      }

      if (!getTemplateField("Solicitor Details", "Phone Number")) {
        delete updatedEoiFormObject.value.data.conveyancerDetails.contactPerson
          .phone;
      }
    }
  };

  const updateCompanyDetails = () => {
    const companyDetailsIsDisabled =
      !hasActiveFields(EoiTemplateGroups.PURCHASE_AS_COMPANY_DETAILS) ||
      !isPurchasingByCompany.value;

    if (companyDetailsIsDisabled) {
      delete updatedEoiFormObject.value.data.companyDetails;
      return;
    }

    const propertiesToDelete = Object.keys(
      updatedEoiFormObject.value.data.companyDetails
    );

    propertiesToDelete.forEach((property) => {
      if (
        !getTemplateField(
          EoiTemplateGroups.PURCHASE_AS_COMPANY_DETAILS,
          getEoiTemplateFieldByProperty(property)
        )
      ) {
        delete updatedEoiFormObject.value.data.companyDetails[property];
      }
    });
  };

  const getEoiTemplateFieldByProperty = (property: string) => {
    switch (property) {
      case "businessIdentifier":
        return EoiTemplateFields.ABN_ACN;
      case "address":
        return EoiTemplateFields.REGISTERED_ADDRESS;
      default:
        return EoiTemplateFields[property.toUpperCase()];
    }
  };

  const scrollToFirstError = () => {
    setTimeout(() => {
      const errors = document.getElementsByClassName("error");

      if (errors.length > 0) {
        scrollTo(errors[0], 100);
      }
    });
  };

  const dispatchEoiAction = (action: string, updateObj = {}) => {
    store
      .dispatch(`eoi/${action}`, updateObj)
      .then((res) => {
        onSuccessAction(res);
        routerPushAfterAction();
      })
      .catch(({ response }) => {
        loading.value = false;
        setHttpError(response);
      });
  };

  const routerPushAfterAction = () => {
    const routeName = isZeroDeposit.value
      ? "EOIRequestPaymentConfirmation"
      : "EOIRequestPayment";

    const params = {
      estateId: props.estateId,
      ...(!isZeroDeposit.value
        ? {
            lotId: props.lotId,
            eoiId: eoiId.value,
          }
        : {}),
    };

    router.push({
      name: routeName,
      params: {
        ...params,
      },
      query: {
        ...(props.businessId ? { businessId: props.businessId } : {}),
      },
    });
  };

  const onSuccessAction = (res: AxiosResponse) => {
    if (isLogged() && !isCustomer.value) return;

    if (isLogged()) {
      void updateUserProfile().catch(() => undefined);
    } else if (!isZeroDeposit.value) {
      LocalStorageService.set("eoiUserData", JSON.stringify(res.data));
    }

    resetHttpErrors();
  };

  const validateFormAndFiles = () => {
    v$.value.$touch();
    purchaserTypeError.value =
      requestAmlInformation.value && !requestForm.purchaserType;
    return !(v$.value.$invalid || !fileInfoValid.value);
  };

  const buildEoISubmitAction = () => {
    const depositType = isZeroDeposit.value ? "ZeroDeposit" : "";
    const registrationStatus =
      isLogged() && isCustomer.value ? "" : "ForUnregisteredCustomer";

    return `submit${depositType}Eoi${registrationStatus}`;
  };
  const proceed = () => {
    resetHttpErrors();

    if (!isTermsAccepted.value) {
      return;
    }

    if (!validateFormAndFiles()) {
      scrollToFirstError();
      return;
    }

    const eoiData = prepareEoiData();

    eoiData.data.customerId = customerId.value;
    eoiData.data.estateId = props.estateId;
    eoiData.data.lotId = props.lotId;

    loading.value = true;

    const eoiAction = buildEoISubmitAction();

    dispatchEoiAction(eoiAction, eoiData);
  };

  function draft() {
    if (!fileInfoValid.value) {
      return;
    }

    const updateObj = prepareEoiData();

    store.dispatch("eoi/saveDraftEoi", updateObj).then(() => {
      void updateUserProfile().catch(() => undefined);
      router.push({
        name: "EOIDraftConfirmation",
        params: {
          estateId: props.estateId,
          lotId: props.lotId,
        },
      });
    });
  }

  const setOnlyCustomersCanSubmitEoiError = ({ data }: AxiosResponse): void => {
    const { ONLY_CUSTOMERS_CAN_SUBMIT_EOI } = EoiHttpMessageErrors;

    if (data.message === ONLY_CUSTOMERS_CAN_SUBMIT_EOI) {
      isEmailBelongsToCustomer.value = false;
      setInfoError(ONLY_CUSTOMERS_CAN_SUBMIT_EOI);
    }
  };

  const setInfoError = (message?: string) => {
    const headerTitle = document.getElementById("header-title");

    headerTitle && scrollTo(headerTitle, 100);
  };

  const setHttpError = (response: AxiosResponse) => {
    resetHttpErrors();

    if (response?.data.statusCode === 403) {
      setOnlyCustomersCanSubmitEoiError(response);
    }
  };

  const resetHttpErrors = () => {
    hasInitialHoldEmailError.value = false;
    isEmailBelongsToCustomer.value = true;
  };

  const updateHaveOwnSolicitor = () => {
    haveOwnSolicitor.value = true;
    clearSolicitor();
  };
  const updateDontHaveOwnSolicitor = () => {
    haveOwnSolicitor.value = false;
    prefillSolicitor();
  };

  const prefillSolicitor = () => {
    requestForm.conveyancerDetails = getZipConveyancerDetails();
  };

  const clearSolicitor = () => {
    requestForm.conveyancerDetails = getEmptyConveyancerDetails();
  };

  function getFirstBuyerEmailClass() {
    return [
      { disabled: isLogged() && isCustomer.value },
      {
        error:
          v$.value.buyersData.firstBuyer.email.$error ||
          !isEmailBelongsToCustomer.value,
      },
    ];
  }

  function showFirstBuyerDocument() {
    return (
      getTemplateField(
        "Buyer details",
        "Upload ID (Drivers license, Passport)"
      ) && requestForm.buyersData.firstBuyer?.documents[0]
    );
  }

  function getFirstBuyerDocumentClass() {
    return {
      "select-file-wrapper-file-selected":
        requestForm.buyersData.firstBuyer?.documents[0]?.name,
      error:
        v$.value.buyersData.firstBuyer.documents[0].link.$error ||
        fileInfo.firstBuyer.isError,
    };
  }

  function showFirstBuyerDocumentName() {
    return (
      requestForm.buyersData.firstBuyer?.documents[0]?.name &&
      !isFileLoading.value
    );
  }

  function getSecondBuyerDocumentClass() {
    return {
      "select-file-wrapper-file-selected":
        requestForm.buyersData.secondBuyer?.documents[0]?.name,
      error:
        v$.value.buyersData.secondBuyer.documents[0].link.$error ||
        fileInfo.secondBuyer.isError,
    };
  }

  function showSecondBuyerDocumentName() {
    return (
      requestForm.buyersData.secondBuyer?.documents[0]?.name &&
      !isFileLoading.value
    );
  }

  function getBankDocumentClass() {
    return {
      "select-file-wrapper-file-selected":
        requestForm.contractConditions.bankApprovalDocument?.name,
      error:
        v$.value.contractConditions.bankApprovalDocument.link.$error ||
        fileInfo.bank.isError,
    };
  }

  function showBankDocumentName() {
    return (
      requestForm.contractConditions.bankApprovalDocument?.name &&
      !isFileLoading.value
    );
  }

const __returned__ = { props, emits, isCustomer, toast, isCreateEioError, isSecondBuyer, subjectToAny, customerId, eoiId, estate, lot, acceptedTypes, showConfirmationModal, loading, isEmailBelongsToCustomer, hasInitialHoldEmailError, userProfile, buyerPassportList, buyerDriverList, documentList, hasFirstBuyerMiddleName, hasSecondBuyerMiddleName, fileSizeError, fileTypeError, imageUploadTips, documentPrivacyTip, transitionName, fileInfo, getEoiTemplateGroups, getTemplateField, eoiTemplate, requestAmlInformation, formLoading, updatedEoiFormObject, isZeroDeposit, isDontHaveSolicitorOpen, haveOwnSolicitor, isTermsAccepted, purchaserTypeError, fileInfoValid, isLotStatusAvailable, get requestForm() { return requestForm }, set requestForm(v) { requestForm = v }, isPurchasingByCompany, purchaserTypes, sourceOfDepositFundsOptions, countryOptions, isEntityPurchaser, amlBuyers, personAmlFields, trustSmsfTypeOptions, partnershipTypeOptions, entityAmlFields, onToggleFirstBuyerMiddleName, onToggleSecondBuyerMiddleName, rules, getFinanceBrokerDetailsRules, v$, setPurchaserType, setAmlField, formatAmlCurrency, parseAmlCurrency, setAmlCurrencyField, getAmlFieldRows, getSelectedOtherFields, getAmlFieldValue, touchAmlField, isAmlFieldInvalid, hasActiveFields, onBack, getUserProfile, setBuyerTypeField, setDefaultDocumentsFieldsFirstBuyer, setConveyancerDetails, setContractConditions, updateUserProfileFromEoi, updateUserProfileDetails, updateUserProfile, addFileToDropDown, setFileByIndex, searchFileInUploaded, formatLandLoanAmountOptions, setBuyerIdType, subjectToFinance, setFinanceStatus, setBuyerState, clearFileInfo, addErrorToFileInfo, isFileLoading, uploadFile, deleteDocument, changeSelectFile, prepareEoiData, updateAmlDetails, updateFirstBuyerDetails, updateSecondBuyerDetails, updateDefaultGroup, updateContractConditions, updateSolicitorDetails, updateCompanyDetails, getEoiTemplateFieldByProperty, scrollToFirstError, dispatchEoiAction, routerPushAfterAction, onSuccessAction, validateFormAndFiles, buildEoISubmitAction, proceed, draft, setOnlyCustomersCanSubmitEoiError, setInfoError, setHttpError, resetHttpErrors, updateHaveOwnSolicitor, updateDontHaveOwnSolicitor, prefillSolicitor, clearSolicitor, getFirstBuyerEmailClass, showFirstBuyerDocument, getFirstBuyerDocumentClass, showFirstBuyerDocumentName, getSecondBuyerDocumentClass, showSecondBuyerDocumentName, getBankDocumentClass, showBankDocumentName, ref, reactive, computed, onMounted, onUnmounted, onBeforeMount, get store() { return store }, get router() { return router }, HeaderCustomer, EOIInfoMessage, EOIDontHaveSolicitor, LotCard, get Cleave() { return Cleave }, get useVuelidate() { return useVuelidate }, get required() { return required }, get email() { return email }, get requiredIf() { return requiredIf }, get numeric() { return numeric }, get minLength() { return minLength }, get minValue() { return minValue }, get helpers() { return helpers }, get validPhoneNumber() { return validPhoneNumber }, DropDown, DropDownFile, CheckboxInput, get allowedDocumentMimeTypes() { return allowedDocumentMimeTypes }, get MaxFileSize() { return MaxFileSize }, ConfirmationModal, get EoiStatus() { return EoiStatus }, get scrollTo() { return scrollTo }, get phoneNumberOption() { return phoneNumberOption }, get LotInterface() { return LotInterface }, get ContractConditions() { return ContractConditions }, get Conveyancer() { return Conveyancer }, get ResponseEoiDetails() { return ResponseEoiDetails }, get changePrimaryColor() { return changePrimaryColor }, get resetPrimaryColor() { return resetPrimaryColor }, get LotStatus() { return LotStatus }, get POSITION() { return POSITION }, get useToast() { return useToast }, InputFile, get validDocumentsTypes() { return validDocumentsTypes }, get isLogged() { return isLogged }, get LocalStorageService() { return LocalStorageService }, get AxiosError() { return AxiosError }, get AxiosResponse() { return AxiosResponse }, get EoiHttpMessageErrors() { return EoiHttpMessageErrors }, get formatPrice() { return formatPrice }, get EoiTemplateGroups() { return EoiTemplateGroups }, get EoiTemplateFields() { return EoiTemplateFields }, get StatesEnum() { return StatesEnum }, InfoMessage, get getUserRole() { return getUserRole }, get getEmptyConveyancerDetails() { return getEmptyConveyancerDetails }, get getEoiRequestForm() { return getEoiRequestForm }, get getZipConveyancerDetails() { return getZipConveyancerDetails }, get capitalizeString() { return capitalizeString }, get changePrimaryColorEoiViews() { return changePrimaryColorEoiViews } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})